import React, { useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom';
import * as XLSX from 'xlsx';

import AddPackageSubscription from '../components/AddPackageSubscription';
import {
    deletePackageSubscription,
    listenToPackageSubscriptions,
    updatePackageSubscription,
} from '../services/packageSubscriptions';
import {
    calculatePackageSubscriptionTotals,
    customerTypeLabel,
    packagePillPresentation,
} from '../services/packageSubscriptionMapper';
import {
    ActionGroup,
    Badge,
    Button,
    CsvButton,
    DataTable,
    EmptyState,
    Field,
    FilterGrid,
    FilterHeader,
    FilterPanel,
    Header,
    HeaderActions,
    IconButton,
    InlineEditor,
    Input,
    PackageBadge,
    Page,
    PaymentStatusEditor,
    PrimaryText,
    ResultCount,
    SecondaryText,
    SectionTitle,
    Select,
    SmallButton,
    StatCard,
    StatLabel,
    StatMeta,
    StatsGrid,
    StatValue,
    Subtitle,
    TableHeader,
    TablePanel,
    TableScroll,
    Title,
    TotalCard,
    TotalPaid,
    TotalValue,
} from './Styles/PackageSubscriptions';

const emptyTotals = calculatePackageSubscriptionTotals([]);

const formatDate = dateValue => {
    if (!dateValue) return '—';

    const date = typeof dateValue?.toDate === 'function'
        ? dateValue.toDate()
        : new Date(dateValue);

    if (Number.isNaN(date.getTime())) return '—';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}.${date.getFullYear()}`;
};

const packageLabel = value => String(value || '')
    .replace(/^Pretplatni(?:c|č)ki paket\s*:\s*/i, '') || 'Nije definisano';

const customerTypeTone = type => {
    if (type === 'company') return 'success';
    if (type === 'person') return 'default';
    return 'warning';
};

const PackageSubscriptions = ({ history }) => {
    const [rows, setRows] = useState([]);
    const [shownRows, setShownRows] = useState([]);
    const [year, setYear] = useState('2027');
    const [packageFilter, setPackageFilter] = useState('');
    const [userSearch, setUserSearch] = useState('');
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [totals, setTotals] = useState(emptyTotals);
    const [showModal, setShowModal] = useState(false);
    const [paymentEditorId, setPaymentEditorId] = useState('');
    const [paymentDraft, setPaymentDraft] = useState(false);
    const [paymentSaving, setPaymentSaving] = useState(false);
    const [descriptionEditorId, setDescriptionEditorId] = useState('');
    const [description, setDescription] = useState('');

    useEffect(() => listenToPackageSubscriptions(year, packageFilter, result => {
        setRows(result);
        setTotals(calculatePackageSubscriptionTotals(result));
    }), [year, packageFilter]);

    useEffect(() => {
        const normalizedUserSearch = userSearch.trim().toLocaleLowerCase('bs');
        const normalizedInvoiceSearch = invoiceSearch.trim().toLocaleLowerCase('bs');

        setShownRows(rows.filter(row => {
            const data = row.data || {};
            const customerText = `${data.user || ''} ${data.company || ''} ${data.email || ''}`
                .toLocaleLowerCase('bs');
            const invoiceText = String(data.invoiceId || '').toLocaleLowerCase('bs');

            return (!normalizedUserSearch || customerText.includes(normalizedUserSearch)) &&
                (!normalizedInvoiceSearch || invoiceText.includes(normalizedInvoiceSearch));
        }));
    }, [rows, userSearch, invoiceSearch]);

    const csvRows = useMemo(() => shownRows.map(row => ({
        Datum: formatDate(row.data.date),
        Kompanija: row.data.company,
        TipKupca: customerTypeLabel(row.data.customer_type),
        Korisnik: row.data.user,
        Email: row.data.email,
        Grad: row.data.city,
        Telefon: row.data.phone,
        Paket: packageLabel(row.data.package_name),
        Plaćeno: row.data.paid ? 'Da' : 'Ne',
        Predračun: row.data.invoiceId,
        NoviKorisnik: row.data.isNewUser ? 'Da' : 'Ne',
        Opis: row.data.description || '',
        DatumPlaćanja: formatDate(row.data.paid_date),
    })), [shownRows]);

    const statistics = [
        {
            label: 'Revicon pretplate',
            value: totals.revicon,
            meta: 'interni Revicon računi',
            accent: '#19a9d5',
            highlight: true,
        },
        {
            label: 'Basic',
            value: totals.plans.basic.total,
            meta: `${totals.plans.basic.paid} plaćeno`,
            paid: true,
            accent: '#54a6d8',
        },
        {
            label: 'Better Value',
            value: totals.plans['better-value'].total,
            meta: `${totals.plans['better-value'].paid} plaćeno`,
            paid: true,
            accent: '#9f7aea',
        },
        {
            label: 'Premium',
            value: totals.plans.premium.total,
            meta: `${totals.plans.premium.paid} plaćeno`,
            paid: true,
            accent: '#e8ba25',
        },
        {
            label: 'Premium Public',
            value: totals.plans['premium-public-sector'].total,
            meta: `${totals.plans['premium-public-sector'].paid} plaćeno`,
            paid: true,
            accent: '#ed8936',
        },
        {
            label: 'Revicon Club',
            value: totals.plans['revicon-club'].total,
            meta: `${totals.plans['revicon-club'].paid} plaćeno`,
            paid: true,
            accent: '#27c24c',
        },
    ];

    const closeInlineEditors = () => {
        setPaymentEditorId('');
        setPaymentSaving(false);
        setDescriptionEditorId('');
        setDescription('');
    };

    const generateFileName = () => {
        const date = new Date();
        return `Pretplate_${date.toISOString().slice(0, 10)}.csv`;
    };

    const exportToExcel = () => {
        if (csvRows.length === 0) {
            window.alert('Nema podataka za izvoz.');
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(csvRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Pretplate');
        XLSX.writeFile(workbook, `Pretplate_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const handleUpdateRow = row => {
        history.push('/edit-package-subscriptions', {
            id: row.id,
            sourceCollection: row.sourceCollection,
        });
    };

    const handleDelete = row => {
        const customer = row.data.company || row.data.user || row.data.email || row.id;
        if (!window.confirm(`Da li ste sigurni da želite obrisati pretplatu za ${customer}?`)) return;

        deletePackageSubscription(row.id, row.sourceCollection)
            .catch(error => {
                console.error(error);
                window.alert('Pretplata nije obrisana. Pokušajte ponovo.');
            });
    };

    const updatePayment = (row, paid) => {
        setPaymentDraft(paid);
        setPaymentSaving(true);
        updatePackageSubscription(row.id, row.sourceCollection, {
            paid,
            paid_date: paid ? new Date().toISOString() : '',
        }).then(() => {
            setPaymentEditorId('');
            setPaymentSaving(false);
        })
            .catch(error => {
                console.error(error);
                setPaymentDraft(row.data.paid === true);
                setPaymentSaving(false);
                window.alert('Status plaćanja nije sačuvan. Pokušajte ponovo.');
            });
    };

    const updateDescription = row => {
        updatePackageSubscription(row.id, row.sourceCollection, { description })
            .then(() => {
                setDescriptionEditorId('');
                setDescription('');
            })
            .catch(error => {
                console.error(error);
                window.alert('Opis nije sačuvan. Pokušajte ponovo.');
            });
    };

    const openDescriptionEditor = row => {
        setDescription(row.data.description || '');
        setDescriptionEditorId(`${row.sourceCollection}_${row.id}`);
        setPaymentEditorId('');
    };

    const openPaymentEditor = row => {
        setPaymentEditorId(`${row.sourceCollection}_${row.id}`);
        setPaymentDraft(row.data.paid === true);
        setPaymentSaving(false);
        setDescriptionEditorId('');
    };

    return (
        <Page>
            <AddPackageSubscription
                showModal={showModal}
                handleShowModal={() => setShowModal(current => !current)}
            />

            <Header>
                <div>
                    <Title>Pretplate</Title>
                    <Subtitle>Godišnje pretplate, naplata i podaci o kupcima na jednom mjestu.</Subtitle>
                </div>
                <HeaderActions>
                    {csvRows.length > 0 && (
                        <CsvButton data={csvRows} filename={generateFileName()}>
                            <i className="fas fa-file-csv" /> CSV
                        </CsvButton>
                    )}
                    <Button type="button" onClick={exportToExcel}>
                        <i className="fas fa-file-excel" /> Excel
                    </Button>
                    <Button type="button" $primary onClick={() => setShowModal(true)}>
                        <i className="fas fa-plus" /> Dodaj pretplatu
                    </Button>
                </HeaderActions>
            </Header>

            <FilterPanel>
                <FilterHeader>
                    <SectionTitle>Filteri</SectionTitle>
                </FilterHeader>
                <FilterGrid>
                    <Field>
                        Godina
                        <Select value={year} onChange={event => {
                            setYear(event.target.value);
                            closeInlineEditors();
                        }}>
                            <option value="2027">2027</option>
                            <option value="2026">2026</option>
                            <option value="2025">2025</option>
                            <option value="">Sve godine</option>
                        </Select>
                    </Field>
                    <Field>
                        Kupac
                        <Input
                            type="search"
                            placeholder="Ime, kompanija ili email"
                            value={userSearch}
                            onChange={event => {
                                setUserSearch(event.target.value);
                                closeInlineEditors();
                            }}
                        />
                    </Field>
                    <Field>
                        Predračun
                        <Input
                            type="search"
                            placeholder="Broj predračuna"
                            value={invoiceSearch}
                            onChange={event => {
                                setInvoiceSearch(event.target.value);
                                closeInlineEditors();
                            }}
                        />
                    </Field>
                    <Field>
                        Paket
                        <Select value={packageFilter} onChange={event => {
                            setPackageFilter(event.target.value);
                            closeInlineEditors();
                        }}>
                            <option value="">Svi paketi</option>
                            <option value="basic">BASIC</option>
                            <option value="bv">BETTER VALUE</option>
                            <option value="premium">PREMIUM</option>
                            <option value="premiumjs">PREMIUM PUBLIC</option>
                            <option value="rc">REVICON CLUB</option>
                        </Select>
                    </Field>
                </FilterGrid>
            </FilterPanel>

            <TotalCard>
                <div>
                    <StatLabel>Ukupno pretplata</StatLabel>
                    <TotalValue>{rows.length}</TotalValue>
                </div>
                <TotalPaid>
                    <strong>{totals.paid}</strong>
                    <span>Plaćeno</span>
                </TotalPaid>
            </TotalCard>

            <StatsGrid>
                {statistics.map(stat => (
                    <StatCard key={stat.label} $accent={stat.accent} $highlight={stat.highlight}>
                        <StatLabel>{stat.label}</StatLabel>
                        <StatValue>{stat.value}</StatValue>
                        <StatMeta $paid={stat.paid}>{stat.meta}</StatMeta>
                    </StatCard>
                ))}
            </StatsGrid>

            <TablePanel>
                <TableHeader>
                    <SectionTitle>Pregled pretplata</SectionTitle>
                    <ResultCount>
                        Prikazano {shownRows.length} od {rows.length}
                    </ResultCount>
                </TableHeader>

                {shownRows.length === 0 ? (
                    <EmptyState>
                        <i className="fas fa-search" />
                        Nema pretplata za odabrane filtere.
                    </EmptyState>
                ) : (
                    <TableScroll>
                        <DataTable>
                            <thead>
                                <tr>
                                    <th>Datum</th>
                                    <th>Kupac</th>
                                    <th>Kontakt</th>
                                    <th>Paket</th>
                                    <th>Status</th>
                                    <th>Predračun</th>
                                    <th>Opis</th>
                                    <th>Datum plaćanja</th>
                                    <th>Akcije</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shownRows.map(row => {
                                    const rowKey = `${row.sourceCollection}_${row.id}`;
                                    const isEditingPayment = paymentEditorId === rowKey;
                                    const isEditingDescription = descriptionEditorId === rowKey;
                                    const packagePill = packagePillPresentation(
                                        row.data.package_code || row.data.package_name,
                                        row.data.year
                                    );

                                    return (
                                        <tr key={rowKey} data-payment-status={row.data.paid ? 'paid' : 'unpaid'}>
                                            <td>{formatDate(row.data.date)}</td>
                                            <td>
                                                <PrimaryText>{row.data.company || row.data.user || '—'}</PrimaryText>
                                                {row.data.company && row.data.user && (
                                                    <SecondaryText>{row.data.user}</SecondaryText>
                                                )}
                                                <SecondaryText>
                                                    <Badge $tone={customerTypeTone(row.data.customer_type)}>
                                                        {customerTypeLabel(row.data.customer_type)}
                                                    </Badge>
                                                </SecondaryText>
                                            </td>
                                            <td>
                                                <PrimaryText>{row.data.email || '—'}</PrimaryText>
                                                <SecondaryText>
                                                    {[row.data.phone, row.data.city].filter(Boolean).join(' · ') || '—'}
                                                </SecondaryText>
                                            </td>
                                            <td>
                                                <PackageBadge
                                                    $background={packagePill.background}
                                                    $foreground={packagePill.foreground}
                                                >
                                                    {packageLabel(row.data.package_name)}
                                                </PackageBadge>
                                            </td>
                                            <td>
                                                {isEditingPayment ? (
                                                    <PaymentStatusEditor $paid={paymentDraft}>
                                                        <label>
                                                            <input
                                                                type="checkbox"
                                                                checked={paymentDraft}
                                                                disabled={paymentSaving}
                                                                onChange={event => updatePayment(row, event.target.checked)}
                                                            />
                                                            <span>{paymentSaving
                                                                ? 'Spremanje…'
                                                                : paymentDraft ? 'Plaćeno' : 'Nije plaćeno'}</span>
                                                        </label>
                                                        <button
                                                            type="button"
                                                            disabled={paymentSaving}
                                                            onClick={() => setPaymentEditorId('')}
                                                        >
                                                            Odustani
                                                        </button>
                                                    </PaymentStatusEditor>
                                                ) : (
                                                    <Badge $tone={row.data.paid ? 'success' : 'danger'}>
                                                        {row.data.paid ? 'Plaćeno' : 'Nije plaćeno'}
                                                    </Badge>
                                                )}
                                            </td>
                                            <td>
                                                <PrimaryText>{row.data.invoiceId || '—'}</PrimaryText>
                                                <SecondaryText>
                                                    <a
                                                        href={`https://revicon.info/api/get-package-pdf?token=${row.data.pdfId || row.id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Otvori PDF
                                                    </a>
                                                </SecondaryText>
                                            </td>
                                            <td>
                                                {isEditingDescription ? (
                                                    <InlineEditor>
                                                        <textarea
                                                            aria-label="Opis pretplate"
                                                            value={description}
                                                            onChange={event => setDescription(event.target.value)}
                                                        />
                                                        <div>
                                                            <SmallButton type="button" onClick={() => updateDescription(row)}>
                                                                Sačuvaj
                                                            </SmallButton>
                                                            <SmallButton
                                                                type="button"
                                                                $secondary
                                                                onClick={() => {
                                                                    setDescriptionEditorId('');
                                                                    setDescription('');
                                                                }}
                                                            >
                                                                Odustani
                                                            </SmallButton>
                                                        </div>
                                                    </InlineEditor>
                                                ) : (
                                                    <SecondaryText title={row.data.description || ''}>
                                                        {row.data.description || 'Bez opisa'}
                                                    </SecondaryText>
                                                )}
                                            </td>
                                            <td>{formatDate(row.data.paid_date)}</td>
                                            <td>
                                                <ActionGroup>
                                                    <IconButton
                                                        type="button"
                                                        onClick={() => handleUpdateRow(row)}
                                                        title="Uredi sve podatke"
                                                        aria-label="Uredi sve podatke"
                                                    >
                                                        <i className="fas fa-pen" />
                                                    </IconButton>
                                                    <IconButton
                                                        type="button"
                                                        onClick={() => openPaymentEditor(row)}
                                                        title="Promijeni status plaćanja"
                                                        aria-label="Promijeni status plaćanja"
                                                    >
                                                        <i className="fas fa-check-circle" />
                                                    </IconButton>
                                                    <IconButton
                                                        type="button"
                                                        onClick={() => openDescriptionEditor(row)}
                                                        title="Uredi opis"
                                                        aria-label="Uredi opis"
                                                    >
                                                        <i className="fas fa-align-left" />
                                                    </IconButton>
                                                    <IconButton
                                                        type="button"
                                                        $danger
                                                        onClick={() => handleDelete(row)}
                                                        title="Obriši pretplatu"
                                                        aria-label="Obriši pretplatu"
                                                    >
                                                        <i className="fas fa-trash" />
                                                    </IconButton>
                                                </ActionGroup>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </DataTable>
                    </TableScroll>
                )}
            </TablePanel>
        </Page>
    );
};

export default withRouter(PackageSubscriptions);
